/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import AvalaraValidationModal from "@eisinc/App/Components/AddressSuggestionCard/AvalaraValidationModal";
import Notify from "@eisinc/App/Components/Notify";
import requestUpdateShipTo from "@eisinc/App/Handlers/UpdateShipTo";
import { xAddressValidationUpdate } from "@eisinc/App/Handlers/XNG/AddressValidationSuggestion";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@eisinc/Overrides/Components/AddressInfoDisplay";
import CustomerAddressForm from "@eisinc/Overrides/Components/CustomerAddressForm";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { Session } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import loadAddressFields from "@insite/client-framework/Store/Data/AddressFields/Handlers/LoadAddressFields";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import updateShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/UpdateShipTo";
import {
    getCurrentShipToState,
    getShipTosDataView,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import loadShipTos from "@insite/client-framework/Store/Pages/Addresses/Handlers/LoadShipTos";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BillToModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { createContext, FC, FormEvent, FunctionComponent, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    shipTosDataView: getShipTosDataView(state, state.pages.addresses.getShipTosParameter),
    getShipTosParameter: state.pages.addresses.getShipTosParameter,
    currentBillTo: getCurrentBillToState(state).value,
    currentShipTo: getCurrentShipToState(state).value,
    countries: getCurrentCountries(state),
    addressFieldDataView: getAddressFieldsDataView(state).value,
    // XNG-Changes
    session: getSession(state),
    b2BCanManageShippingAddress: (getSettingsCollection(state).customSettings as any).b2BCanManageShippingAddress,
    addressValidationState: (state as CustomAppState).xAddressValidation.addressValidationState,
});

const mapDispatchToProps = {
    setCurrentShipTo,
    loadShipTos,
    updateShipTo,
    // XNG-Change
    requestUpdateShipTo,
    loadAddressFields,
    xAddressValidationUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddressBookStyles {
    gridContainer?: GridContainerProps;
    titleTextGridItem?: GridItemProps;
    addressHeaderGridItem?: GridItemProps;
    titleText?: TypographyProps;
    noAddressesText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    addressCardGridItem?: GridItemProps;
    addressFormModal?: ModalPresentationProps;
    addressCard?: AddressCardStyles;
}

interface AddressCardStyles {
    gridContainer?: GridContainerProps;
    addressInfoDisplayGridItem?: GridItemProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    actionsGridItem?: GridItemProps;
    actionOverflowIcon?: IconPresentationProps;
    actionUseAsShippingAddressButton?: ButtonPresentationProps;
    actionEditLink?: LinkPresentationProps;
    bottomBorderGridItem?: GridItemProps;
    bottomBorder?: InjectableCss;
    overflowMenu?: OverflowMenuPresentationProps;
}

export const addressBookStyles: AddressBookStyles = {
    gridContainer: { gap: 20 },
    titleTextGridItem: { width: 12 },
    addressHeaderGridItem: { width: 12 },
    titleText: { variant: "h5" },
    noAddressesText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    addressCardGridItem: { width: 12 },
    addressCard: {
        gridContainer: { gap: 20 },
        addressInfoDisplayGridItem: { width: [10, 10, 5, 7, 7] },
        actionsGridItem: {
            width: [2, 2, 7, 5, 5],
            css: css`
                justify-content: flex-end;
            `,
        },
        actionUseAsShippingAddressButton: { color: "secondary" },
        actionEditLink: {
            css: css`
                display: block;
                margin: 20px auto 0 auto;
            `,
        },
        bottomBorderGridItem: { width: 12 },
        bottomBorder: {
            css: css`
                height: 1px;
                background-color: ${getColor("common.border")};
                width: 100%;
            `,
        },
        overflowMenu: {
            cssOverrides: {
                menu: css`
                    width: 210px;
                `,
            },
        },
    },
    addressFormModal: { sizeVariant: "medium" },
};

const styles = addressBookStyles;

type ContextType = {
    isSettingShipTo: boolean;
    setIsSettingShipTo: (value: boolean) => void;
};

export const AddressBookContext = createContext<ContextType>({
    isSettingShipTo: false,
    setIsSettingShipTo: value => {},
});

const AddressBook: FC<Props> = ({
    id,
    shipTosDataView,
    loadShipTos,
    setCurrentShipTo,
    updateShipTo,
    addressFieldDataView,
    countries,
    currentBillTo,
    currentShipTo,
    getShipTosParameter,
    // XNG-Change
    requestUpdateShipTo,
    session,
    loadAddressFields,
    b2BCanManageShippingAddress,
    addressValidationState,
}) => {
    const [isSettingShipTo, setIsSettingShipTo] = useState(false);

    useEffect(() => {
        if (!shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos();
        }
    });

    // XNG-Change
    useEffect(() => {
        if (!addressFieldDataView) {
            loadAddressFields();
        }
    }, [addressFieldDataView]);

    let component;
    if (shipTosDataView.isLoading || !shipTosDataView.value || !addressFieldDataView) {
        component = (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    } else if (shipTosDataView.value.length > 0) {
        component = shipTosDataView.value.map(shipTo => (
            <GridItem {...styles.addressCardGridItem} key={shipTo.id.toString()}>
                <AddressCard
                    shipTo={shipTo}
                    setCurrentShipTo={setCurrentShipTo}
                    updateShipTo={updateShipTo}
                    billToAddressFields={addressFieldDataView?.billToAddressFields}
                    shipToAddressFields={addressFieldDataView?.shipToAddressFields}
                    countries={countries}
                    currentBillTo={currentBillTo}
                    currentShipTo={currentShipTo}
                    requestUpdateShipTo={requestUpdateShipTo}
                    session={session}
                    // XNG-Change
                    {...({ b2BCanManageShippingAddress } as any)}
                    {...({ addressValidationState } as any)}
                />
            </GridItem>
        ));
    } else {
        component = (
            <StyledWrapper {...styles.centeringWrapper}>
                {/* XNG-Change use notify */}
                {getShipTosParameter.filter ? (
                    <Notify
                        messageType="danger"
                        notifyContainerOverrides={css`
                            max-width: 100%;
                        `}
                        body={siteMessage("Addresses_NoResultsFound")}
                    />
                ) : (
                    <Notify
                        messageType="danger"
                        notifyContainerOverrides={css`
                            max-width: 100%;
                        `}
                        body={siteMessage("Addresses_NoAddressesFound")}
                    />
                )}
            </StyledWrapper>
        );
    }

    return (
        <AddressBookContext.Provider value={{ isSettingShipTo, setIsSettingShipTo }}>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.titleTextGridItem}>
                    <Typography {...styles.titleText}>{translate("Address Book")}</Typography>
                </GridItem>
                <Zone contentId={id} zoneName="Content00" />
                {component}
            </GridContainer>
        </AddressBookContext.Provider>
    );
};

interface AddressCardProps {
    shipTo: ShipToModel;
    currentBillTo?: BillToModel;
    currentShipTo?: ShipToModel;
    countries?: CountryModel[];
    billToAddressFields?: AddressFieldDisplayCollectionModel;
    shipToAddressFields?: AddressFieldDisplayCollectionModel;
    setCurrentShipTo: Props["setCurrentShipTo"];
    updateShipTo: Props["updateShipTo"];
    // XNG-Change
    requestUpdateShipTo: Props["requestUpdateShipTo"];
    session: Session;
    b2BCanManageShippingAddress?: any;
    addressValidationState?: any;
}

const AddressCard: FunctionComponent<AddressCardProps> = (props: AddressCardProps) => {
    const { shipTo, b2BCanManageShippingAddress, addressValidationState } = props;
    const componentStyles: AddressCardStyles = styles.addressCard || {};

    return (
        <GridContainer {...componentStyles.gridContainer}>
            <GridItem
                {...componentStyles.addressInfoDisplayGridItem}
                data-test-selector="addressInfoDisplay_addressBook"
            >
                <AddressInfoDisplay
                    firstName={shipTo.firstName}
                    lastName={shipTo.lastName}
                    companyName={shipTo.companyName}
                    attention={shipTo.attention}
                    address1={shipTo.address1}
                    address2={shipTo.address2}
                    address3={shipTo.address3}
                    address4={shipTo.address4}
                    city={shipTo.city}
                    state={shipTo.state ? shipTo.state.abbreviation : undefined}
                    postalCode={shipTo.postalCode}
                    country={shipTo.country ? shipTo.country.abbreviation : undefined}
                    phone={shipTo.phone}
                    fax={shipTo.fax}
                    email={shipTo.email}
                    extendedStyles={componentStyles.addressInfoDisplay}
                    // XNG-Change
                    {...({ addressFields: props.shipToAddressFields } as any)}
                />
            </GridItem>
            <GridItem {...componentStyles.actionsGridItem}>
                <AddressActions
                    shipTo={shipTo}
                    updateShipTo={props.updateShipTo}
                    setCurrentShipTo={props.setCurrentShipTo}
                    currentBillTo={props.currentBillTo}
                    currentShipTo={props.currentShipTo}
                    countries={props.countries}
                    shipToAddressFields={props.shipToAddressFields}
                    billToAddressFields={props.billToAddressFields}
                    requestUpdateShipTo={requestUpdateShipTo}
                    session={props.session}
                    // XNG-Change
                    {...({ b2BCanManageShippingAddress } as any)}
                    {...({ addressValidationState } as any)}
                />
            </GridItem>
            <GridItem {...componentStyles.bottomBorderGridItem}>
                <StyledWrapper {...componentStyles.bottomBorder} />
            </GridItem>
        </GridContainer>
    );
};

interface AddressActionsProps {
    shipTo: ShipToModel;
    currentBillTo?: BillToModel;
    currentShipTo?: ShipToModel;
    countries?: CountryModel[];
    billToAddressFields?: AddressFieldDisplayCollectionModel;
    shipToAddressFields?: AddressFieldDisplayCollectionModel;
    setCurrentShipTo: Props["setCurrentShipTo"];
    updateShipTo: Props["updateShipTo"];
    // XNG-Change
    requestUpdateShipTo: Props["requestUpdateShipTo"];
    session: Session;
    b2BCanManageShippingAddress?: any;
    addressValidationState?: any;
}

const AddressActions: FunctionComponent<AddressActionsProps> = (props: AddressActionsProps) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    // <XNG-Change>
    const [formIsSubmitting, setFormIsSubmitting] = useState(false);
    const [validationAddressSpinnerVisible, setValidationAddressSpinnerVisible] = useState(false);
    //  </XNG-Change>

    const addressBookContext = useContext(AddressBookContext);
    const toasterContext = useContext(ToasterContext);
    const [typedAddress, setTypedAddress] = React.useState<ShipToModel>();
    const avalaraValidation: any = useRef(null);

    const componentStyles: AddressCardStyles = styles.addressCard || {};

    // XNG-Change
    const dispatch = useAppDispatch();

    const {
        shipTo,
        currentBillTo,
        currentShipTo,
        countries,
        shipToAddressFields,
        b2BCanManageShippingAddress,
        session,
        addressValidationState,
    } = props;

    const useAsShippingAddressHandler = (e: any) => {
        e.preventDefault();

        addressBookContext.setIsSettingShipTo(true);

        props.setCurrentShipTo({
            shipToId: shipTo.id,
            onSuccess: () => {
                addressBookContext.setIsSettingShipTo(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onSuccess?.();
                }
            },
        });
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const editClickHandler = () => {
        // XNG-Change
        dispatch({
            type: "xAddressValidation/update",
            data: { addressValidationState: false },
        });
        xAddressValidationUpdate({ addressValidationState: false });
        setValidationAddressSpinnerVisible(false);
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        e.preventDefault();

        modalCloseHandler();
    };

    const isAdmin = session.userRoles.includes("Administrator");
    const isB2B = currentBillTo?.properties?.isB2B === "True";
    const canManageShipping = b2BCanManageShippingAddress === "true";
    const allowEdit = isAdmin || canManageShipping;
    const isRequestUpdate = !allowEdit;

    const formSubmitHandler = (_: FormEvent<HTMLFormElement>, address: ShipToModel) => {
        if (!currentBillTo || !currentShipTo) {
            return;
        }
        setTypedAddress(address);
        setValidationAddressSpinnerVisible(true);
        setFormIsSubmitting(true);
        avalaraValidation.current.validate(address).then(
            (updatedAddress: ShipToModel) => {
                avalaraValidation.current.countyValidation(updatedAddress, (county: any) => {
                    continueSavingAddress(updatedAddress, county);
                    setValidationAddressSpinnerVisible(false);
                });
            },
            (err: any) => {
                setFormIsSubmitting(false);
                setValidationAddressSpinnerVisible(false);
            },
        );
    };

    const continueSavingAddress = (address: ShipToModel, county: any) => {
        if (!currentBillTo || !currentShipTo) {
            return;
        }

        if (!isRequestUpdate) {
            props.updateShipTo({
                billToId: currentBillTo.id,
                shipTo: {
                    ...shipTo,
                    ...address,
                    properties: {
                        ...shipTo.properties,
                        county,
                    },
                },
                expand: ["validation"],
            });
        } else {
            props.requestUpdateShipTo({
                billToId: currentBillTo.id,
                shipTo: {
                    ...shipTo,
                    ...address,
                    properties: {
                        ...shipTo.properties,
                        county,
                    },
                },
                expand: ["validation"],
            });
        }
        modalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: isRequestUpdate ? "Address update requested." : "Address updated.",
        });
    };

    return (
        <>
            <Hidden above="sm">
                <OverflowMenu position="end" {...componentStyles.overflowMenu}>
                    <Clickable
                        onClick={useAsShippingAddressHandler}
                        disabled={addressBookContext.isSettingShipTo || shipTo.id === currentShipTo?.id}
                    >
                        {translate("Use as Shipping Address")}
                    </Clickable>
                    <Clickable onClick={editClickHandler}>
                        {!isRequestUpdate ? translate("Edit") : translate("Request Update")}
                    </Clickable>
                </OverflowMenu>
            </Hidden>
            <Hidden below="md">
                <Button
                    {...componentStyles.actionUseAsShippingAddressButton}
                    onClick={useAsShippingAddressHandler}
                    disabled={addressBookContext.isSettingShipTo || shipTo.id === currentShipTo?.id}
                    sizeVariant="small"
                    data-test-selector="useAs_Shipping_Address"
                >
                    {translate("Use as Shipping Address")}
                </Button>
                <Link {...componentStyles.actionEditLink} onClick={editClickHandler}>
                    {!isRequestUpdate ? translate("Edit") : translate("Request Update")}
                </Link>
            </Hidden>
            <Modal
                {...styles.addressFormModal}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                headline={
                    isRequestUpdate
                        ? translate("Request Update Shipping Information")
                        : translate("Edit Shipping Information")
                }
                sizeVariant="small"
            >
                {/* XNG-Change: add validationAddressSpinnerVisible check */}
                {!validationAddressSpinnerVisible && countries && shipToAddressFields && !addressValidationState && (
                    <CustomerAddressForm
                        address={shipTo}
                        countries={countries}
                        addressFieldDisplayCollection={shipToAddressFields}
                        onCancel={formCancelHandler}
                        onSubmit={formSubmitHandler}
                        isSubmitting={formIsSubmitting}
                    />
                )}
                {validationAddressSpinnerVisible && (
                    <StyledWrapper
                        css={css`
                            width: 100%;
                            height: 100%;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                        `}
                    >
                        <LoadingSpinner color="primary" />
                    </StyledWrapper>
                )}
            </Modal>
            {/* XNG-Change */}
            <AvalaraValidationModal ref={avalaraValidation} address={typedAddress} />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddressBook),
    definition: {
        group: "Addresses",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
